import sortBy from 'lodash/sortBy'

import {
  CarriedBrand,
  LocationType,
  ProductLine,
} from '__generated__/atlas-types'

import {
  StyledAddress,
  StyledContent,
  StyledPill,
  StyledPillContainer,
  StyledSection,
  StyledSectionWithFlex,
  StyledSubSection,
  StyledText,
  StyledTitle,
} from './LocationWidgetViewMode.styles'
import {
  getFormattedClienteleInfo,
  getFormattedLocation,
  getGoogleMapsUrl,
  useClienteleInfo,
} from './LocationWidgetViewMode.utils'
import { RetailerProfileLocation } from 'features/LeadGen/RetailerProfile/retailerProfilePage.types'

type LocationWidgetViewModeProps = {
  location: RetailerProfileLocation
}

const LocationAddress = ({ location }: LocationWidgetViewModeProps) => {
  const { formattedAddress, formattedLocationInfo } =
    getFormattedLocation(location)
  const mapsUrl = getGoogleMapsUrl(location)
  const addressContent = (
    <>
      <div>{formattedAddress}</div>
      <div>{formattedLocationInfo}</div>
      <div>{location.zip}</div>
      <div>{location.phone}</div>
    </>
  )
  return (
    <StyledSection>
      <StyledTitle>Address</StyledTitle>
      <StyledAddress
        className="noTranslate"
        data-testid={`location_addres1_address2_${location?.id}`}
        as={mapsUrl ? 'a' : undefined}
        href={mapsUrl ?? undefined}
        target={mapsUrl ? '_blank' : undefined}
        rel={mapsUrl ? 'noopener noreferrer' : undefined}
        style={
          mapsUrl
            ? {
                cursor: 'pointer',
                textDecoration: 'none',
                color: 'var(--color-secondary-800)',
              }
            : undefined
        }
      >
        {addressContent}
      </StyledAddress>
    </StyledSection>
  )
}

const Clientele = ({
  clienteleInfo,
}: {
  clienteleInfo: RetailerProfileLocation['clienteleInfo']
}) => {
  const {
    basicInfoFormatted,
    clienteleDescriptionItems,
    clienteleInterestItems,
  } = getFormattedClienteleInfo(clienteleInfo)

  return (
    <div>
      {!!basicInfoFormatted && (
        <StyledSection>
          <StyledTitle>Demographics</StyledTitle>
          <StyledText>{basicInfoFormatted}</StyledText>
        </StyledSection>
      )}
      {clienteleDescriptionItems.length > 0 && (
        <StyledSection>
          <StyledTitle>Described As</StyledTitle>
          {clienteleDescriptionItems.map((item, index) => (
            <StyledText key={index}>{item}</StyledText>
          ))}
        </StyledSection>
      )}
      {clienteleInterestItems.length > 0 && (
        <StyledSection>
          <StyledTitle>Shops For</StyledTitle>
          {clienteleInterestItems.map((item, index) => (
            <StyledText key={index}>{item}</StyledText>
          ))}
        </StyledSection>
      )}
    </div>
  )
}

const Categories = ({ categories }: { categories: ProductLine[] }) => {
  const categoryNames = sortBy(categories, 'name')?.map(
    (category) => category.name,
  )
  return (
    <StyledSubSection>
      <StyledTitle>Categories</StyledTitle>
      <StyledSectionWithFlex>
        <StyledPillContainer>
          {categoryNames.map((name, index) => (
            <StyledPill key={index}>{name}</StyledPill>
          ))}
        </StyledPillContainer>
      </StyledSectionWithFlex>
    </StyledSubSection>
  )
}
const CarriedBrands = ({
  carriedBrands,
}: {
  carriedBrands: CarriedBrand[]
}) => {
  const carriedBrandNames = carriedBrands?.map(
    (carriedBrand) => carriedBrand.name,
  )
  return (
    <StyledSubSection>
      <StyledTitle>Brands Carried</StyledTitle>
      <StyledSection>
        <StyledPillContainer>
          {carriedBrandNames?.map((name, index) => (
            <StyledPill key={index} className="notranslate">
              {name}
            </StyledPill>
          ))}
        </StyledPillContainer>
      </StyledSection>
    </StyledSubSection>
  )
}

const LocationWidgetViewMode = ({ location }: LocationWidgetViewModeProps) => {
  const { hasInfo: hasClienteleInfo } = useClienteleInfo(location.clienteleInfo)

  if (!location) return null

  const categories =
    !!location.categories && location.categories.length > 0
      ? location.categories
      : undefined

  const brandsCarried =
    !!location.brandsCarried && location.brandsCarried.length > 0
      ? location.brandsCarried
      : undefined

  const hasMarketInfo =
    !!location.type ||
    !!location.yearEstablished ||
    !!(location.wholesaleRange?.min || location.wholesaleRange?.max)

  return (
    <StyledContent>
      <div>
        {location.name && (
          <StyledSection>
            <StyledTitle>Location Name</StyledTitle>
            <StyledText className="notranslate">{location?.name}</StyledText>
            {hasMarketInfo && (
              <div style={{ display: 'flex', gap: '3px' }}>
                {location.type && (
                  <StyledText style={{ fontSize: 'var(--size-font-small)' }}>
                    {location?.type === LocationType.STORE ? 'Store' : 'Office'}
                    ,
                  </StyledText>
                )}
                {location.yearEstablished && (
                  <StyledText
                    className="notranslate"
                    style={{ fontSize: 'var(--size-font-small)' }}
                  >
                    {location?.yearEstablished}
                  </StyledText>
                )}
              </div>
            )}
          </StyledSection>
        )}
        <LocationAddress location={location} />
      </div>
      {hasMarketInfo && (
        <div>
          {(location.wholesaleRange?.min || location.wholesaleRange?.max) && (
            <StyledSection>
              <StyledTitle>Wholesale Price</StyledTitle>
              <StyledText className="notranslate">
                {`$${location.wholesaleRange?.min} - $${location.wholesaleRange?.max}`}
              </StyledText>
            </StyledSection>
          )}
          {categories && (
            <div>
              <Categories categories={categories} />
            </div>
          )}
        </div>
      )}
      {brandsCarried && (
        <div>
          <CarriedBrands carriedBrands={brandsCarried} />
        </div>
      )}
      {hasClienteleInfo && (
        <div>
          <Clientele clienteleInfo={location?.clienteleInfo} />
        </div>
      )}
    </StyledContent>
  )
}

export default LocationWidgetViewMode
